import { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  BookOpen,
  CheckCircle2,
  Clock,
  GraduationCap,
  Lock,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useStudent } from "../../context/StudentContext";
import { api, type SemesterStatus } from "../../lib/api";
import { useStudySubjects } from "../../lib/useStudySubjects";
import { useStudyProgress } from "../../lib/useStudyProgress";
import { StudySubjectLoading } from "../../components/study/StudySubjectLoading";
import { StatusBanner } from "../../components/ui/StatusBanner";
import { Badge } from "../../components/ui/Badge";
import { CourseCard as CourseCardBase } from "../../components/ui/CourseCard";

export function SemesterCoursesPage() {
  const { semesterNumber } = useParams<{ semesterNumber: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const semNum = Number(semesterNumber);
  const { tokens } = useAuth();
  const token = tokens?.accessToken;
  const { openUpgrade, getSemesterStatus, isAdminPreview, semesterStatuses } = useStudent();

  const { subjects, loading: loadingSubjects, error } = useStudySubjects();
  const studyProgress = useStudyProgress();
  const progressById = new Map((studyProgress?.courses ?? []).map((c) => [c.course_id, { done: c.done, total: c.total }]));
  const [localStatus, setLocalStatus] = useState<SemesterStatus | null>(null);
  const [userStream, setUserStream] = useState<string>("common");
  const [loadingProfile, setLoadingProfile] = useState(true);

  // Validate semester number: must be a positive integer
  if (isNaN(semNum) || semNum <= 0) {
    return <Navigate to="/student/study" replace />;
  }

  // Load student profile stream & initial semester status fallback
  useEffect(() => {
    if (!token) {
      setLoadingProfile(false);
      return;
    }

    let cancelled = false;
    Promise.all([
      api.payments.semesterStatus(token).catch(() => [] as SemesterStatus[]),
      api.profile(token).catch(() => null),
    ])
      .then(([statuses, profile]) => {
        if (cancelled) return;
        const currentSem = statuses.find((s) => s.semester === semNum);
        if (currentSem) setLocalStatus(currentSem);

        if (profile?.streamName || profile?.streamCode) {
          const raw = (profile.streamCode || profile.streamName || "").toLowerCase();
          setUserStream(raw.includes("natural") ? "natural" : (raw.includes("social") ? "social" : "common"));
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingProfile(false);
      });

    return () => {
      cancelled = true;
    };
  }, [token, semNum]);

  // Reactive semester status from StudentContext (real-time updates without reload)
  const semesterStatus = getSemesterStatus(semNum) || localStatus;
  const isUnlocked = isAdminPreview || (semesterStatus?.unlocked ?? false);
  const hasPending = Boolean(semesterStatus?.hasPendingRequest || semesterStatus?.has_pending_request);
  const isAvailable = semesterStatus ? (semesterStatus.isAvailable ?? semesterStatus.is_available ?? semesterStatus.status !== "coming_soon") : true;

  // Distinct available semesters across statuses and subjects
  const availableSemesters = useMemo(() => {
    const sems = new Set<number>([1, 2]);
    for (const st of semesterStatuses) {
      if (st.semester && st.semester > 0) sems.add(st.semester);
    }
    for (const sub of subjects) {
      if (sub.semester && sub.semester > 0) sems.add(sub.semester);
    }
    return Array.from(sems).sort((a, b) => a - b);
  }, [semesterStatuses, subjects]);

  // Filter courses for this semester (exclude unassigned courses)
  const semesterCourses = useMemo(() => {
    return subjects.filter((s) => (s.semester ?? 1) === semNum);
  }, [subjects, semNum]);

  // Group into common courses vs stream courses:
  // Common: universal curriculum (stream === "common" or no streamId)
  const commonCourses = useMemo(() => {
    return semesterCourses.filter((s) => s.stream === "common" || !s.streamId);
  }, [semesterCourses]);

  // Stream: student's stream only, or all stream courses if admin preview
  const streamCourses = useMemo(() => {
    return semesterCourses.filter((s) => {
      if (s.stream === "common" || !s.streamId) return false;
      if (isAdminPreview) return true;
      const sStream = (s.stream || s.streamCode || "").toLowerCase();
      const sName = (s.streamName || "").toLowerCase();

      if (userStream === "natural") {
        return sStream === "natural" || sName.includes("natural");
      }
      if (userStream === "social") {
        return sStream === "social" || sName.includes("social");
      }
      return false;
    });
  }, [semesterCourses, userStream, isAdminPreview]);

  return (
    <div className="max-w-5xl mx-auto space-y-6 pb-12 animate-in fade-in duration-200">
      {/* Back button & Breadcrumb */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => location.key !== "default" ? navigate(-1) : navigate("/student/study")}
          className="inline-flex items-center gap-1.5 text-xs font-benton font-semibold text-muted-foreground hover:text-foreground transition group"
        >
          <ArrowLeft size={14} className="group-hover:-translate-x-0.5 transition-transform" />
          Back to Study Hub
        </button>

        {/* Quick switch between available semesters */}
        <div className="flex items-center gap-1 text-xs overflow-x-auto pb-1 max-w-full">
          {availableSemesters.map((num) => (
            <Link
              key={num}
              to={`/student/study/semester/${num}`}
              className={`px-2.5 py-1 rounded-xs font-semibold whitespace-nowrap transition ${
                semNum === num
                  ? "bg-primary text-on-accent shadow-xs"
                  : "bg-secondary text-muted-foreground hover:text-foreground"
              }`}
            >
              Semester {num}
            </Link>
          ))}
        </div>
      </div>

      {/* Page Header */}
      <div className="surface-sm rounded-xs border border-border p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-gradient-to-br from-card to-secondary/30">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold uppercase tracking-wider text-primary">
              Freshman Year
            </span>
            <span className="text-muted-foreground text-xs">&middot;</span>
            <span className="text-xs font-semibold text-muted-foreground">
              Semester {semNum}
            </span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold font-display text-foreground">
            Semester {semNum} Courses
          </h1>
          <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
            Choose a course to explore learning modules, take practice quizzes, read revision notes, and solve verified past exams.
          </p>
        </div>

        {isUnlocked ? (
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-xs bg-success/10 border border-success/30 text-success self-start sm:self-center shrink-0">
            <CheckCircle2 size={16} />
            <span className="text-xs font-bold font-benton">Unlocked</span>
          </div>
        ) : !isAvailable ? (
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-xs bg-secondary border border-border text-muted-foreground self-start sm:self-center shrink-0">
            <span className="text-xs font-bold font-benton">Coming Soon</span>
          </div>
        ) : hasPending ? (
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-xs bg-muted border border-border text-muted-foreground self-start sm:self-center shrink-0">
            <Clock size={15} />
            <span className="text-xs font-bold font-benton">Awaiting Review</span>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => openUpgrade({ semester: semNum, stream: userStream })}
            className="btn btn-primary text-xs py-2 px-3.5 shadow-xs self-start sm:self-center shrink-0 flex items-center gap-1.5"
          >
            <Lock size={13} />
            Unlock Semester {semNum}
          </button>
        )}
      </div>

      {/* Unlock / Awaiting Review Banner when locked and available */}
      

      {/* When coming soon: show a simple message instead of the course list */}
      {!isAvailable ? (
        <div className="text-center py-16 bg-card rounded-xs border border-border p-8 max-w-lg mx-auto space-y-3">
          <div className="w-12 h-12 rounded-full bg-secondary text-muted-foreground flex items-center justify-center mx-auto">
            <BookOpen size={24} />
          </div>
          <h3 className="text-lg font-bold font-display text-foreground">Semester {semNum} is coming soon</h3>
          <p className="text-xs text-muted-foreground leading-relaxed">
            Curriculum materials and courses for Semester {semNum} are currently being finalized. They will appear here once published.
          </p>
          <div className="pt-2">
            <button type="button" onClick={() => location.key !== "default" ? navigate(-1) : navigate("/student/study")} className="btn btn-secondary text-xs py-1.5 px-3.5">
              Back to Study Hub
            </button>
          </div>
        </div>
      ) : loadingSubjects ? (
        <StudySubjectLoading />
      ) : error ? (
        <StatusBanner type="error" message={error} />
      ) : semesterCourses.length === 0 ? (
        <div className="text-center py-16 bg-card rounded-xs border border-dashed border-border p-8">
          <GraduationCap size={40} className="mx-auto text-muted-foreground/50 mb-3" />
          <h3 className="font-bold text-lg">No courses in Semester {semNum}</h3>
          <p className="text-sm text-muted-foreground max-w-md mx-auto mt-1">
            Courses for this semester are being curated and will appear here once published.
          </p>
          <div className="pt-4">
            <button type="button" onClick={() => location.key !== "default" ? navigate(-1) : navigate("/student/study")} className="btn btn-secondary text-xs py-1.5 px-3">
              Back to Study Hub
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Common Courses Section */}
          {commonCourses.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h2 className="text-section font-bold font-display text-foreground">
                  Common Courses ({commonCourses.length})
                </h2>
                <span className="text-xs text-muted-foreground">Universal curriculum</span>
              </div>

              <div className="grid grid-cols-[repeat(auto-fill,minmax(16.25rem,1fr))] gap-4">
                {commonCourses.map((s) => (
                  <CourseCard
                    key={s.id || s.slug}
                    course={s}
                    semesterNumber={semNum}
                    progress={progressById.get(s.id)}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Stream-Specific Courses Section */}
          {streamCourses.length > 0 && (
            <div className="space-y-3 pt-2">
              <div className="flex items-center justify-between">
                <h2 className="text-section font-bold font-display text-foreground">
                  Stream Courses ({streamCourses.length})
                </h2>
                <span className="text-xs text-muted-foreground capitalize">
                  {isAdminPreview ? "All streams (Admin preview)" : (userStream !== "common" ? `${userStream} Science` : "Discipline specific")}
                </span>
              </div>

              <div className="grid grid-cols-[repeat(auto-fill,minmax(16.25rem,1fr))] gap-4">
                {streamCourses.map((s) => (
                  <CourseCard
                    key={s.id || s.slug}
                    course={s}
                    semesterNumber={semNum}
                    progress={progressById.get(s.id)}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function CourseCard({
  course,
  semesterNumber,
  progress,
}: {
  course: any;
  semesterNumber: number;
  progress?: { done: number; total: number };
}) {
  const isPremium = Boolean(course.isPremium);
  return (
    <CourseCardBase
      to={`/student/study/${course.slug}`}
      state={{ fromSemester: semesterNumber }}
      title={course.name}
      subtitle={course.code}
      icon={<BookOpen size={20} />}
      chip={isPremium ? <Badge variant="warning">Premium</Badge> : <Badge variant="success">Free</Badge>}
      progress={progress && progress.total > 0 ? { label: "Chapters completed", done: progress.done, total: progress.total } : undefined}
    />
  );
}
